/**
 * Light/dark theme preference persistence — stores the choice under the `theme`
 * localStorage key. The initial theme is always light unless the user has
 * explicitly toggled (and thus persisted) a choice; the OS `prefers-color-scheme`
 * is deliberately ignored. Wired into `UIProvider` in `main.tsx`
 * (`defaultTheme` seeds initial render, `onThemeChange` writes back on toggle).
 */
type ThemePreference = 'light' | 'dark';

const STORAGE_KEY = 'theme';

/** Initial theme: stored choice → light (OS preference intentionally ignored). */
export function resolveInitialTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through to light default.
  }
  return 'light';
}

/** Persist the chosen theme so it survives reloads. */
export function persistTheme(theme: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Best-effort — ignore storage failures (private mode / quota).
  }
}
